import * as Yup from "yup";

import { phoneRegExp } from "../../../utils/contsant";

const linkButtonSchema = Yup.object().shape({
  title: Yup.string(),
  url: Yup.string(),
  open_in_popup: Yup.boolean(),
  comment_for_ai: Yup.string(),
});

const greetingButtonSchema = Yup.object().shape({
  title: Yup.string(),
  url: Yup.string(),
  open_in_popup: Yup.boolean(),
});

const hasCompleteButtons = (buttons) =>
  Array.isArray(buttons) &&
  buttons.length > 0 &&
  buttons.every(
    (button) =>
      typeof button?.title === "string" &&
      button.title.trim() !== "" &&
      typeof button?.url === "string" &&
      button.url.trim() !== ""
  );

const createRequiredButtonsSchema = ({
  schema,
  minMessage,
  requiredFieldsMessage,
  testName,
}) =>
  Yup.array()
    .of(schema)
    .min(1, minMessage)
    .test(testName, requiredFieldsMessage, hasCompleteButtons);

const createRequiredLinkButtonsSchema = (testName) =>
  createRequiredButtonsSchema({
    schema: linkButtonSchema,
    minMessage: "* Add at least one button link",
    requiredFieldsMessage: "Each button link must have both title and URL",
    testName,
  });

const createRequiredGreetingButtonsSchema = (testName) =>
  createRequiredButtonsSchema({
    schema: greetingButtonSchema,
    minMessage: "* Add at least one greeting button",
    requiredFieldsMessage: "Each greeting button must have both title and URL",
    testName,
  });

const createBaseSchema = ({
  includePhorestIntegration = true,
  includeMessengerIntegration = true,
} = {}) =>
  Yup.object().shape({
    greetingMessage: Yup.string()
      .trim()
      .required("* Please enter greeting message"),
    instagramDM: Yup.boolean(),
    whatsAppDm: Yup.boolean(),
    integrationsPhorestEnabled: Yup.boolean(),
    integrationsMessengerEnabled: Yup.boolean(),
    instagramAccessToken: Yup.string().when("instagramDM", {
      is: true,
      then: (schema) =>
        schema.trim().required("* Please enter Instagram Access Token"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    whatsAppAccessToken: Yup.string().when("whatsAppDm", {
      is: true,
      then: (schema) =>
        schema.trim().required("* Please enter whatsApp Access Token"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    integrationsPhorestRegion: Yup.string().when("integrationsPhorestEnabled", {
      is: (enabled) => includePhorestIntegration && enabled === true,
      then: (schema) =>
        schema
          .trim()
          .uppercase()
          .oneOf(["EU"], "Only the EU Phorest region is supported")
          .required("* Please enter Phorest region"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    integrationsPhorestBusinessId: Yup.string().when("integrationsPhorestEnabled", {
      is: (enabled) => includePhorestIntegration && enabled === true,
      then: (schema) => schema.trim().max(256).required("* Please enter Phorest Business ID"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    integrationsPhorestBranchId: Yup.string().when("integrationsPhorestEnabled", {
      is: (enabled) => includePhorestIntegration && enabled === true,
      then: (schema) => schema.trim().max(256).required("* Please enter Phorest Branch ID"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    integrationsPhorestCurrency: Yup.string().when("integrationsPhorestEnabled", {
      is: (enabled) => includePhorestIntegration && enabled === true,
      then: (schema) =>
        schema.trim().matches(/^[A-Za-z]{3}$/, "Currency must use a three-letter code").required("* Please enter Phorest currency"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    integrationsPhorestApiUsername: Yup.string().when("integrationsPhorestEnabled", {
      is: (enabled) => includePhorestIntegration && enabled === true,
      then: (schema) =>
        schema.trim().max(320).matches(/^global\//, "API Username must start with global/").required("* Please enter Phorest API Username"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    integrationsPhorestApiPassword: Yup.string()
      .max(4096, "Phorest API Password cannot exceed 4096 characters")
      .when(
      ["integrationsPhorestEnabled", "integrationsPhorestApiPasswordConfigured"],
      {
        is: (enabled, configured) =>
          includePhorestIntegration && enabled === true && configured !== true,
        then: (schema) =>
          schema
            .required("* Please enter Phorest API Password")
            .test(
              "phorest-password-not-blank",
              "* Please enter Phorest API Password",
              (value) => typeof value === "string" && value.trim().length > 0
            ),
        otherwise: (schema) => schema.nullable().notRequired(),
      }
      ),
    integrationsMessengerPageAccessToken: Yup.string()
      .max(4096, "Facebook Page Access Token cannot exceed 4096 characters")
      .when(
        ["integrationsMessengerEnabled", "integrationsMessengerPageAccessTokenConfigured"],
        {
          is: (enabled, configured) =>
            includeMessengerIntegration &&
            enabled === true &&
            configured !== true,
          then: (schema) =>
            schema
              .required("* Please enter Facebook Page Access Token")
              .test(
                "messenger-token-not-blank",
                "* Please enter Facebook Page Access Token",
                (value) => typeof value === "string" && value.trim().length > 0
              ),
          otherwise: (schema) => schema.nullable().notRequired(),
        }
      ),
    greeting_buttons_enabled: Yup.boolean(),
    greeting_buttons: Yup.array().of(greetingButtonSchema),
    link_buttons_enabled: Yup.boolean(),
    link_buttons: Yup.array().of(linkButtonSchema),
  });

export const BusinessFormSchema = ({
  hasClientScope,
  isClient,
  includePhorestIntegration = true,
  includeMessengerIntegration = true,
}) =>
  createBaseSchema({ includePhorestIntegration, includeMessengerIntegration }).concat(
    Yup.object().shape({
      name: Yup.string()
        .trim()
        .required("* Please enter name")
        .max(50, "name cannot exceed 50 characters"),
      idClient: Yup.mixed().test(
        "client-required",
        "Please select idClient",
        (value) => {
          if (isClient && hasClientScope) {
            return true;
          }

          if (!value) {
            return false;
          }

          if (typeof value === "string") {
            return value.trim().length > 0;
          }

          if (typeof value === "object") {
            return Object.keys(value).length > 0;
          }

          return false;
        }
      ),
      websiteUrl: Yup.string().trim().required("* Please enter website URL"),
      assistantId: Yup.string().trim().required("* Please enter assistant id"),
      hotelEmail: Yup.string()
        .email("Invalid email")
        .trim()
        .required("* Please enter hotel email"),
      phone: Yup.string()
        .trim()
        .matches(phoneRegExp, {
          message: "* Phone number is not valid (eg. (+XX)XXXXXXXXXX)",
          excludeEmptyString: true,
        })
        .required("* Please enter mobile number"),
      address: Yup.string()
        .trim()
        .required("* Please enter address")
        .max(150, "Address cannot exceed 100 characters"),
      accessToken: Yup.string().trim().when("apiType", {
        is: "mews",
        then: Yup.string().required("* Please enter access token"),
      }),
      clientToken: Yup.string().trim().when("apiType", {
        is: "mews",
        then: Yup.string().required("* Please enter client token"),
      }),
      mewsClientId: Yup.string().trim().when("apiType", {
        is: "mews",
        then: Yup.string().required("* Please enter client id"),
      }),
      avvioUsername: Yup.string().trim().when("apiType", {
        is: "avvio",
        then: Yup.string().required("* Please enter user name"),
      }),
      avvioPassword: Yup.string().trim().when("apiType", {
        is: "avvio",
        then: Yup.string().required("* Please enter password"),
      }),
      hotelRedirectUrl: Yup.string().trim().when("apiType", {
        is: "noReservation",
        then: Yup.string().required("* Please enter hotel redirect URL"),
      }),
      greeting_buttons: Yup.array()
        .of(greetingButtonSchema)
        .when("greeting_buttons_enabled", {
          is: true,
          then: () =>
            createRequiredGreetingButtonsSchema(
              "greeting-buttons-required-fields"
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      link_buttons: Yup.array()
        .of(linkButtonSchema)
        .when(["rivet", "link_buttons_enabled"], {
          is: (rivet, linkButtonsEnabled) => !rivet && linkButtonsEnabled,
          then: () =>
            createRequiredLinkButtonsSchema("link-buttons-required-fields"),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
  );

export const limitedEditorSchema = () =>
  createBaseSchema({
    includePhorestIntegration: false,
    includeMessengerIntegration: false,
  }).concat(
    Yup.object().shape({
      greeting_buttons: Yup.array()
        .of(greetingButtonSchema)
        .when("greeting_buttons_enabled", {
          is: true,
          then: () =>
            createRequiredGreetingButtonsSchema(
              "limited-greeting-buttons-required-fields"
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      link_buttons: Yup.array().notRequired(),
    })
  );
